import { useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import SimpleNavigation from '../../sections/SimpleNavigation';
import HeroSection from '../../sections/HeroSection';
import HowItWorksSection from '../../sections/HowItWorksSection';
import InsightsSection from '../../sections/InsightsSection';
import CXOProfilesSection from '../../sections/CXOProfilesSection';
import PackagesSection from '../../sections/PackagesSection';
import MatchingEngineSection from '../../sections/MatchingEngineSection';
import ProposalSection from '../../sections/ProposalSection';
import WorkspaceSection from '../../sections/WorkspaceSection';
import ImpactSection from '../../sections/ImpactSection';
import TrustSection from '../../sections/TrustSection';
import TestimonialsSection from '../../sections/TestimonialsSection';
import SecuritySection from '../../sections/SecuritySection';
import PricingSection from '../../sections/PricingSection';
import FAQSection from '../../sections/FAQSection';
import CTASection from '../../sections/CTASection';

gsap.registerPlugin(ScrollTrigger);

export default function LandingPage() {
  // Global snap configuration for pinned sections
  useEffect(() => {
    const timer = setTimeout(() => {
      const pinned = ScrollTrigger.getAll()
        .filter((st) => st.vars.pin)
        .sort((a, b) => a.start - b.start);

      const maxScroll = ScrollTrigger.maxScroll(window);

      if (!maxScroll || pinned.length === 0) return;

      const pinnedRanges = pinned.map((st) => ({
        start: st.start / maxScroll,
        end: (st.end ?? st.start) / maxScroll,
        center: (st.start + ((st.end ?? st.start) - st.start) * 0.5) / maxScroll,
      }));

      ScrollTrigger.create({
        snap: {
          snapTo: (value: number) => {
            const inPinned = pinnedRanges.some(
              (r) => value >= r.start - 0.02 && value <= r.end + 0.02
            );

            if (!inPinned) return value;

            const target = pinnedRanges.reduce(
              (closest, r) =>
                Math.abs(r.center - value) < Math.abs(closest - value)
                  ? r.center
                  : closest,
              pinnedRanges[0]?.center ?? 0
            );

            return target;
          },
          duration: { min: 0.15, max: 0.35 },
          delay: 0,
          ease: 'power2.out',
        },
      });
    }, 100);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    return () => {
      ScrollTrigger.getAll().forEach((st) => st.kill());
    };
  }, []);

  return (
    <div className="relative bg-navy min-h-screen">
      <SimpleNavigation />
      <main className="relative pt-16 lg:pt-20">
        <HeroSection />
        <HowItWorksSection />
        <InsightsSection />
        <CXOProfilesSection />
        <PackagesSection />
        <MatchingEngineSection />
        <ProposalSection />
        <WorkspaceSection />
        <ImpactSection />
        <TrustSection />
        <TestimonialsSection />
        <SecuritySection />
        <PricingSection />
        <FAQSection />
        <CTASection />
      </main>
    </div>
  );
}
